/* eslint-disable import/no-extraneous-dependencies */
import 'whatwg-fetch'; // Polyfill

export default class CreativeChallengeDataManager {
  /**
   * Constructor
   *
   * @param {string} id
   *  The identifier of the creative challenge.
 *   @param {string} filesDirPath
   *  The path to the directory containing the challenge files.
   * @param {array} galleries
   *  The list of galleries available for the creative challenge
   */
  constructor(id, filesDirPath, galleries, options = {}) {
    this.id = id;
    this.filesDirPath = filesDirPath;
    this.galleries = galleries;
    const defaultOptions = {
    };
    this.options = { ...defaultOptions, ...options };
    this.entriesData = null;
    this.entriesDataById = null;
    this.galleriesData = {};
  }

  getDataFilePath() {
    return `${this.filesDirPath}/${this.id}.json`;
  }

  getGalleryDataFilePath(galleryId) {
    return `${this.filesDirPath}/${this.id}-${galleryId}.json`;
  }

  getImagesPath() {
    return `${this.filesDirPath}/files`;
  }

  getPhotoRoutePrefix() {
    return this.options.photoRoutePrefix ?? `${this.id}-`;
  }

  getDefaultGallery() {
    return this.galleries[0];
  }

  isValidGalleryId(id) {
    return this.galleries.includes(id);
  }

  async getEntriesData() {
    if (!this.entriesData) {
      this.entriesData = await this.loadEntriesData();
    }
    return this.entriesData;
  }

  async getEntriesDataById() {
    if (!this.entriesDataById) {
      const entriesData = await this.getEntriesData();
      this.entriesDataById = Object.fromEntries(entriesData.map((entry) => [entry.id, entry]));
    }
    return this.entriesDataById;
  }

  async getGalleryData(galleryId) {
    if (!this.galleriesData[galleryId]) {
      this.galleriesData[galleryId] = await this.loadGalleryData(galleryId);
    }
    return this.galleriesData[galleryId];
  }

  /**
   * Loads the creative challenge entries from the data file.
   *
   * Fetches and parses the JSON data file, and builds the URLs to the different image formats, and
   * the route to the detail view of the entry.
   *
   * @return {Promise<*>}
   *  A promise that resolves to the creative challenge items.
   */
  async loadEntriesData() {
    const dataFile = this.getDataFilePath();
    const imagesPath = this.getImagesPath();

    return window.fetch(dataFile, {
      cache: 'no-cache',
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Could not read ${dataFile}.`);
      }
      return response;
    }).then((response) => response.json())
      .then((data) => data.items)
      .then((items) => items.map(
        (item) => ({
          ...item,
          full: `${imagesPath}/xl/${item.id}.jpg`,
          image: `${imagesPath}/lg/${item.id}.jpg`,
          miniImage: `${imagesPath}/md/${item.id}.jpg`,
          thumbnail: `${imagesPath}/sm/${item.id}.jpg`,
          miniThumbnail: `${imagesPath}/xs/${item.id}.jpg`,
          route: `#${this.getPhotoRoutePrefix()}${item.id}`,
        })
      ));
  }

  /**
   * Fetches the creative challenge gallery from the data file.
   *
   * The gallery data file lists the IDs of the items in the gallery.
   *
   * @param {string} galleryId
   *   The identifier of the gallery.
   * @return {Promise<any>}
   *   A promise that resolves to the IDs of the items in the gallery.
   */
  async loadGalleryIds(galleryId){
    return window.fetch(this.getGalleryDataFilePath(galleryId), {
      cache: 'no-cache',
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Could not read ${dataFile}.`);
      }
      return response;
    }).then((response) => response.json())
      .then((data) => data.ids);
  }

  async loadGalleryData(galleryId) {
    const ids = await this.loadGalleryIds(galleryId);
    const entriesById = await this.getEntriesDataById();
    return ids.map((id) => entriesById[id]);
  }
}
